import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type JobCostEstimateLaunchContextValue = {
    /** Non-null while Job Details is mounted — global header shows the $ control. */
    launcher: (() => void) | null;
    /**
     * Register the callback that opens the job cost estimate modal.
     * Pass `null` on unmount. Must use updater form internally: storing a function in
     * useState requires `setState(() => fn)` or React treats `fn` as an updater and runs it.
     */
    registerLauncher: (fn: (() => void) | null) => void;
};

const JobCostEstimateLaunchContext = createContext<JobCostEstimateLaunchContextValue | null>(null);

export function JobCostEstimateLaunchProvider({ children }: { children: React.ReactNode }) {
    const [launcher, setLauncher] = useState<(() => void) | null>(null);

    const registerLauncher = useCallback((fn: (() => void) | null) => {
        setLauncher(() => fn);
    }, []);

    const value = useMemo(
        () => ({ launcher, registerLauncher }),
        [launcher, registerLauncher]
    );

    return (
        <JobCostEstimateLaunchContext.Provider value={value}>{children}</JobCostEstimateLaunchContext.Provider>
    );
}

export function useJobCostEstimateLaunch(): JobCostEstimateLaunchContextValue {
    const ctx = useContext(JobCostEstimateLaunchContext);
    if (!ctx) {
        throw new Error('useJobCostEstimateLaunch must be used within JobCostEstimateLaunchProvider');
    }
    return ctx;
}
